import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { FilterIcon, CheckboxIcon, CheckboxOutlineIcon } from "app/icons";
import React, { useState } from "react";

import { useStyles } from "./styles";

type BaseProps<T extends string> = {
  label: string;
  options: readonly T[];
  getOptionLabel: (value: T) => string;
  getOptionColor?: (value: T) => string;
  secondaryPlainLabel?: boolean;
  variant?: "filter" | "field";
  menuWidth?: number;
  menuMaxHeight?: number;
};

type SingleProps<T extends string> = BaseProps<T> & {
  multiple?: false;
  value: T;
  onChange: (value: T) => void;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
};

type MultipleProps<T extends string> = BaseProps<T> & {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
};

type Props<T extends string> = SingleProps<T> | MultipleProps<T>;

const CHECKBOX_SIZE = 20;

export function FilterSelect<T extends string>(props: Props<T>) {
  const {
    label,
    options,
    getOptionLabel,
    getOptionColor,
    secondaryPlainLabel,
    menuWidth = 160,
    menuMaxHeight = 240,
  } = props;
  const styles = useStyles({ menuWidth, menuMaxHeight });
  const multiple = Boolean(props.multiple);
  const isField = props.variant === "field";
  const selectedValues = multiple ? props.value : [props.value];
  const [open, setOpen] = useState(false);
  const ChevronIcon =
    isField && open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;

  const handleChange = (event: SelectChangeEvent<T | T[]>) => {
    const nextValue = event.target.value;

    if (props.multiple) {
      props.onChange(
        (typeof nextValue === "string" ? nextValue.split(",") : nextValue) as T[]
      );
      return;
    }

    props.onChange(nextValue as T);
  };

  const getTriggerLabel = () => {
    if (!props.multiple) {
      return getOptionLabel(props.value);
    }

    const selected = props.value;

    if (selected.length === 0 || selected.length === options.length) {
      return getOptionLabel(options[0]);
    }

    if (selected.length === 1) {
      return getOptionLabel(selected[0]);
    }

    return `${getOptionLabel(selected[0])}, ...`;
  };

  const renderOptionLabel = (option: T) => {
    const optionLabel = getOptionLabel(option);
    const optionColor = getOptionColor?.(option);

    if (!optionColor) {
      if (!secondaryPlainLabel) {
        return optionLabel;
      }

      return <span className={styles.plainOption}>{optionLabel}</span>;
    }

    return (
      <span
        className={styles.optionChip}
        style={{ backgroundColor: optionColor }}
      >
        {optionLabel}
      </span>
    );
  };

  const fieldSelectProps = isField
    ? {
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
      }
    : {};

  return (
    <FormControl
      size="small"
      className={isField ? `${styles.root} ${styles.fieldRoot}` : styles.root}
    >
      <Select
        multiple={multiple}
        value={props.value}
        onChange={handleChange}
        displayEmpty
        className={isField ? `${styles.select} ${styles.fieldSelect}` : styles.select}
        IconComponent={() => null}
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
          variant: "menu",
          MenuListProps: {
            autoFocusItem: false,
            className: isField ? styles.fieldMenuList : styles.menuList,
          },
          PaperProps: {
            className: isField ? `${styles.menu} ${styles.fieldMenu}` : styles.menu,
          },
          ...(isField
            ? {
                anchorOrigin: { vertical: "bottom" as const, horizontal: "right" as const },
                transformOrigin: { vertical: "top" as const, horizontal: "right" as const },
              }
            : {}),
        }}
        renderValue={() => (
          <Box className={isField ? styles.fieldSelectValue : styles.selectValue}>
            {isField ? null : <FilterIcon className={styles.filterIcon} />}
            <span className={isField ? styles.fieldSelectText : styles.selectText}>
              {isField ? getTriggerLabel() : `${label}: ${getTriggerLabel()}`}
            </span>
            <ChevronIcon
              className={
                isField ? styles.fieldSelectChevron : styles.selectChevron
              }
            />
          </Box>
        )}
        {...fieldSelectProps}
      >
        {options.map((option) => {
          const isSelected = selectedValues.includes(option);
          const singleRenderOption = !props.multiple
            ? (props as SingleProps<T>).renderOption
            : null;

          return (
            <MenuItem key={option} value={option} className={styles.menuItem}>
              <Box
                className={
                  isField ? styles.fieldMenuItemContent : styles.menuItemContent
                }
              >
                {multiple ? (
                  <Checkbox
                    className={styles.checkbox}
                    checked={isSelected}
                    disableRipple
                    icon={
                      <CheckboxOutlineIcon
                        width={CHECKBOX_SIZE}
                        height={CHECKBOX_SIZE}
                      />
                    }
                    checkedIcon={
                      <CheckboxIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />
                    }
                  />
                ) : null}
                {singleRenderOption ? (
                  singleRenderOption(option, isSelected)
                ) : isField ? (
                  <span
                    className={
                      isSelected ? styles.optionSelected : styles.optionLabel
                    }
                  >
                    {renderOptionLabel(option)}
                  </span>
                ) : (
                  renderOptionLabel(option)
                )}
                {isField && isSelected ? (
                  <CheckIcon className={styles.checkIcon} />
                ) : null}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

